import { useEffect } from 'react';
import {
  LEADERBOARD_SLOT_ID,
  MOBILE_BOTTOM_SLOT_ID,
  MPU_SLOT_ID,
  SKYSCRAPER_LEFT_SLOT_ID,
  SKYSCRAPER_RIGHT_SLOT_ID,
  TOP_BANNER_SLOT_ID,
} from '@cx-web/feature-gam-advertising';
import { escapeJsonForInlineScript, removeRepubPrefix } from '@cx-web/utils';

import { type Repub } from '@cx-modules/constants';
import { type DeviceType } from '@cx-modules/types';

declare global {
  interface Window {
    // `setTargeting` is optional: only `cmd` is guaranteed at push time (Content Ignite's own
    // script provides `setTargeting` once it loads and starts draining the queue).
    __ciads?: {
      cmd: Array<() => void>;
      setTargeting?: (key: string, value: string[], configId?: number) => void;
    };
  }
}

// Provided verbatim by Content Ignite/ad ops — do not reformat.
const IAB_TCF_LOCATOR_STUB = `!function(){let e=()=>{let e="__tcfapiLocator",t=[],a=window,n=a,s,o;for(;n;){try{if(n.frames[e]){s=n;break}}catch(c){}if(n===a.top)break;n=n.parent}s||(!function t(){let n=a.document,s=!!a.frames[e];if(!s){if(n.body){let o=n.createElement("iframe");o.style.cssText="display:none",o.name=e,n.body.appendChild(o)}else setTimeout(t,5)}return!s}(),a.__tcfapi=function e(...a){if(!a.length)return t;"setGdprApplies"===a[0]?a.length>3&&2===parseInt(a[1],10)&&"boolean"==typeof a[3]&&(o=a[3],"function"==typeof a[2]&&a[2]("set",!0)):"ping"===a[0]?"function"==typeof a[2]&&a[2]({gdprApplies:o,cmpLoaded:!1,cmpStatus:"stub"}):t.push(a)},a.addEventListener("message",function e(t){let a="string"==typeof t.data,n={};if(a)try{n=JSON.parse(t.data)}catch(s){}else n=t.data;let o="object"==typeof n&&null!==n?n.__tcfapiCall:null;o&&window.__tcfapi(o.command,o.version,function(e,n){let s={__tcfapiReturn:{returnValue:e,success:n,callId:o.callId}};t&&t.source&&t.source.postMessage&&t.source.postMessage(a?JSON.stringify(s):s,"*")},o.parameter)},!1))};"undefined"!=typeof module?module.exports=e:e()}();`;

const CONTENT_IGNITE_SCRIPT_SRC = 'https://cdn.tagdeliver.com/cipt/19208.js';

type GamAdSlotId =
  | typeof LEADERBOARD_SLOT_ID
  | typeof MOBILE_BOTTOM_SLOT_ID
  | typeof MPU_SLOT_ID
  | typeof SKYSCRAPER_LEFT_SLOT_ID
  | typeof SKYSCRAPER_RIGHT_SLOT_ID
  | typeof TOP_BANNER_SLOT_ID;

// Slot-level targeting for the 6 ad placements specified in EU-5620 (Table Tennis / Combat Sports
// migration) — ported from the real, currently-deployed oc-uk reference implementation
// (packages/theme/src/js/content-ignite-ads.js `slotConfigs`), NOT a googletag-mirroring assumption.
// `slotId` is typed against the same TOP_BANNER_SLOT_ID/SKYSCRAPER_LEFT_SLOT_ID/SKYSCRAPER_RIGHT_SLOT_ID/
// LEADERBOARD_SLOT_ID/MPU_SLOT_ID/MOBILE_BOTTOM_SLOT_ID constants GoogleAdUnit.tsx/AdHeaderSlot.astro
// already import from packages/features/gam-advertising/src/adUnitSetup.ts, purely for traceability —
// it is never read to do DOM wiring here, exactly as in oc-uk: Content Ignite's dashboard-side tag
// config (keyed by contentIgniteConfigIds below) is what maps a CONFIG_ID to a page element, not
// client code. Importing the constants (rather than re-typing the literals) means a future rename in
// adUnitSetup.ts is a compile error here instead of a silently stale traceability comment.
export interface ContentIgniteSlotConfig {
  contentIgniteConfigIds: number[];
  placement: 'top_leaderboard' | 'inline_article' | 'sticky_sidebar' | 'inline_article_square' | 'sticky_footer';
  pos: 'ATF' | 'BTF';
  position?: 'left' | 'right';
  slotId: GamAdSlotId;
}

const slotConfigs: ContentIgniteSlotConfig[] = [
  { slotId: TOP_BANNER_SLOT_ID, contentIgniteConfigIds: [18795], placement: 'top_leaderboard', pos: 'ATF' },
  {
    slotId: LEADERBOARD_SLOT_ID,
    contentIgniteConfigIds: [18904, 18905, 18906, 18907],
    placement: 'inline_article',
    pos: 'ATF',
  },
  {
    slotId: SKYSCRAPER_LEFT_SLOT_ID,
    contentIgniteConfigIds: [18800],
    placement: 'sticky_sidebar',
    pos: 'ATF',
    position: 'left',
  },
  {
    slotId: SKYSCRAPER_RIGHT_SLOT_ID,
    contentIgniteConfigIds: [18801],
    placement: 'sticky_sidebar',
    pos: 'ATF',
    position: 'right',
  },
  { slotId: MPU_SLOT_ID, contentIgniteConfigIds: [18796, 18803], placement: 'inline_article_square', pos: 'BTF' },
  { slotId: MOBILE_BOTTOM_SLOT_ID, contentIgniteConfigIds: [18802], placement: 'sticky_footer', pos: 'ATF' },
];

// Exported so the real `slotConfigs` behaviour and the multi-configId branch can both be tested
// directly.
export const buildSlotLevelTargetingCalls = (configs: ContentIgniteSlotConfig[]): string =>
  configs
    .flatMap(config =>
      config.contentIgniteConfigIds.flatMap(configId => {
        const calls = [
          `window.__ciads.setTargeting('placement', ${JSON.stringify([config.placement])}, ${configId});`,
          `window.__ciads.setTargeting('pos', ${JSON.stringify([config.pos])}, ${configId});`,
        ];
        if (config.position) {
          calls.push(`window.__ciads.setTargeting('position', ${JSON.stringify([config.position])}, ${configId});`);
        }
        return calls;
      }),
    )
    .join('\n  ');

// Ported verbatim from oc-uk's `formatPagePath` (content-ignite-ads.js): capitalizes each path
// segment, joins with `_` at increasing specificity, caps at 40 chars, and treats `/` as the
// homepage. Deliberately repub-agnostic — oc-uk itself is a root-prefix repub (no URL prefix ever
// reaches this function there), so it has no opinion on stripping one. The caller (the
// `ContentIgnite` component below) is responsible for passing an already-unprefixed path via
// `removeRepubPrefix`: prefixed repubs (e.g. US → `/us/football`) must not leak their prefix into
// this function's output, or `page_path` shape drifts between repub families for what's meant to be
// the same oc-uk-mirroring targeting field.
export const formatPagePath = (urlPath: string): string[] => {
  if (!urlPath || urlPath === '/') {
    return ['Homepage'];
  }

  const segments = urlPath.split('/').filter(segment => segment.length > 0);
  const capitalizedSegments = segments.map(segment => segment.charAt(0).toUpperCase() + segment.slice(1));
  const results: string[] = [];
  let accumulated = '';

  for (let i = 0; i < capitalizedSegments.length; i++) {
    accumulated = i === 0 ? capitalizedSegments[0] : `${accumulated}_${capitalizedSegments[i]}`;
    if (accumulated.length >= 40) {
      results.push(accumulated.substring(0, 40));
      break;
    }
    results.push(accumulated);
  }

  return results;
};

// TABLET falls back to 'desktop', matching oc-uk's `validDevice` fallback and the ticket's own
// Desktop/Tablet size bucketing (EU-5620's CSS-selector table has no distinct tablet targeting).
const resolveContentIgniteDevice = (deviceType: DeviceType): 'desktop' | 'mobile' =>
  deviceType === 'MOBILE' ? 'mobile' : 'desktop';

// Entry script is appended from inside the stub's own inline script (not a sibling <script src>) so
// the stub always runs first — React 19 can hoist/reorder sibling script tags. Slot-level targeting
// (static — a pure function of the constant `slotConfigs`) is appended in this SAME inline script for
// the same reason, and initializes `window.__ciads.cmd` itself
// (`window.__ciads = window.__ciads || { cmd: [] }`) per Content Ignite's documented pattern
// (https://docs.contentignite.com/publishers/js-api) — this component may run before Content
// Ignite's own entry script has loaded and created that queue, so the push must not assume it
// already exists. Kept as a module-level constant (not rebuilt per render) since none of it depends
// on props.
const slotLevelTargetingCalls = buildSlotLevelTargetingCalls(slotConfigs);

const CONTENT_IGNITE_STATIC_SCRIPT = `${IAB_TCF_LOCATOR_STUB}
(function(){var s=document.createElement("script");s.src=${JSON.stringify(
  CONTENT_IGNITE_SCRIPT_SRC,
)};s.async=true;s.setAttribute("data-testid","content-ignite-entry-script");document.head.appendChild(s);})();
window.__ciads = window.__ciads || { cmd: [] };${
  slotLevelTargetingCalls
    ? `
window.__ciads.cmd.push(function () {
  ${slotLevelTargetingCalls}
});`
    : ''
}`;

// Page-level targeting (repub/page_path/device) MUST be emitted synchronously, in the rendered
// script itself — not only from a client effect. Two reasons, both hit in production:
//   1. atlas-astro renders this component with no `client:*` directive (ChromeLayout.astro), so
//      Astro ships zero JS for it and any client effect never runs there at all — the static script
//      is the ONLY delivery mechanism on that app.
//   2. Even on RR7 (which does hydrate), the entry script above loads async and can drain
//      `window.__ciads.cmd` before hydration completes — or hydration can error and never run —
//      so a client-effect-only design risks the very first page view's ad requests going out with
//      no repub/page_path/device KVPs, which is exactly what the cmd-queue pattern exists to avoid.
// All three values are known at render time (server or client), so there's no reason to defer them.
// `escapeJsonForInlineScript` (not raw `JSON.stringify`) because `page_path` is derived from the
// request path — an unescaped `<` in a crafted path segment could otherwise break out of this
// <script> (see Breadcrumbs.tsx for the same class of fix). Array form (e.g. `[repub]`) overwrites
// rather than merges any stale targeting value, matching the real, currently-deployed reference
// implementation (oc-uk's packages/theme/src/js/content-ignite-ads.js) — NOT a googletag-mirroring
// assumption; both the array form and the raw (not lowercased) repub code are independently
// confirmed there and in the public JS API docs.
const buildContentIgniteBootstrap = (repub: Repub, deviceType: DeviceType, pathname: string): string => {
  const unprefixedPathname = removeRepubPrefix(pathname, repub);
  const pageLevelTargetingCalls = [
    `window.__ciads.setTargeting('repub', ${escapeJsonForInlineScript([repub])});`,
    `window.__ciads.setTargeting('page_path', ${escapeJsonForInlineScript(formatPagePath(unprefixedPathname))});`,
    `window.__ciads.setTargeting('device', ${escapeJsonForInlineScript([resolveContentIgniteDevice(deviceType)])});`,
  ].join('\n  ');

  return `${CONTENT_IGNITE_STATIC_SCRIPT}
window.__ciads.cmd.push(function () {
  ${pageLevelTargetingCalls}
});`;
};

export const ContentIgnite = ({
  deviceType,
  enabled,
  pathname,
  repub,
}: {
  deviceType: DeviceType;
  enabled: boolean;
  pathname: string;
  repub: Repub;
}) => {
  // Refreshes page-level targeting on a client-side (SPA) navigation. This component (via
  // Head -> PageTemplate, apps/atlas/app/routes/$/route.tsx) stays mounted across the RR7 `$`
  // route's navigations, but a <script>'s innerHTML doesn't re-execute once already parsed, so the
  // static render above can't self-update. This effect is purely additive — the render above already
  // delivers correct targeting for the first paint (both apps) and for atlas-astro (which never
  // hydrates this component, so this effect simply never runs there, and doesn't need to). On first
  // mount here it re-pushes the same values the static script just pushed; harmless; `setTargeting`'s
  // array form overwrites rather than merges.
  useEffect(() => {
    if (!enabled) return;

    const unprefixedPathname = removeRepubPrefix(pathname, repub);

    window.__ciads = window.__ciads || { cmd: [] };
    window.__ciads.cmd.push(() => {
      window.__ciads?.setTargeting?.('repub', [repub]);
      window.__ciads?.setTargeting?.('page_path', formatPagePath(unprefixedPathname));
      window.__ciads?.setTargeting?.('device', [resolveContentIgniteDevice(deviceType)]);
    });
  }, [deviceType, enabled, pathname, repub]);

  if (!enabled) {
    return null;
  }

  return (
    <script
      dangerouslySetInnerHTML={{ __html: buildContentIgniteBootstrap(repub, deviceType, pathname) }}
      id="content-ignite-tcf-stub"
    />
  );
};
